import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './TeamAdmin.css';

function TeamAdmin() {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserIsSuperuser, setCurrentUserIsSuperuser] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteAcceptUrl, setInviteAcceptUrl] = useState('');
  const [inviteEmailSent, setInviteEmailSent] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const res = await api.get('/api/v1/auth/team/members/');
      setMembers(res.data.members || []);
      setCurrentUserId(res.data.current_user_id || null);
      setCurrentUserIsSuperuser(!!res.data.current_user_is_superuser);
    } catch (err) {
      console.error('Failed to fetch team members', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchInvites = async () => {
    try {
      setLoadingInvites(true);
      const res = await api.get('/api/v1/auth/team/invites/');
      setInvites(res.data.invites || []);
    } catch (err) {
      console.error('Failed to fetch invites', err);
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchInvites();
  }, []);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    setInviteAcceptUrl('');
    setInviteEmailSent(true);
    setLinkCopied(false);
    setInviteLoading(true);
    try {
      const res = await api.post('/api/v1/auth/team/invites/send/', { email: inviteEmail });
      setInviteSuccess(res.data.email_sent ? `Invite email sent to ${inviteEmail}` : `Invite created — share the link below`);
      setInviteEmailSent(res.data.email_sent);
      setInviteAcceptUrl(res.data.accept_url || '');
      setInviteEmail('');
      fetchInvites();
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteAcceptUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await api.delete(`/api/v1/auth/team/invites/${inviteId}/`);
      fetchInvites();
    } catch (err) {
      console.error('Failed to revoke invite', err);
    }
  };

  const handleRemoveMember = async (userId, username) => {
    if (!window.confirm(`Remove team member "${username}"? They will lose access immediately.`)) return;
    try {
      await api.delete(`/api/v1/auth/team/members/${userId}/`);
      fetchMembers();
    } catch (err) {
      console.error('Failed to remove member', err);
    }
  };

  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return (user.username?.[0] || '?').toUpperCase();
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const pendingInvites = invites.filter((i) => i.status === 'pending' && !i.is_expired);
  const pastInvites = invites.filter((i) => i.status !== 'pending' || i.is_expired);

  return (
    <div className="team-admin-container">
      {/* Page Header */}
      <div className="team-admin-header">
        <div className="team-admin-header-text">
          <h1 className="team-admin-title">Team Management</h1>
          <p className="team-admin-subtitle">Manage team members and pending invitations</p>
        </div>
        <button className="btn-invite" onClick={() => { setShowInviteModal(true); setInviteError(''); setInviteSuccess(''); setInviteEmail(''); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor" />
          </svg>
          Invite Member
        </button>
      </div>

      {/* Stats Row */}
      <div className="team-stats-row">
        <div className="team-stat-card">
          <div className="team-stat-value">{loadingMembers ? '—' : members.length}</div>
          <div className="team-stat-label">Active Members</div>
        </div>
        <div className="team-stat-card">
          <div className="team-stat-value">{loadingInvites ? '—' : pendingInvites.length}</div>
          <div className="team-stat-label">Pending Invites</div>
        </div>
        <div className="team-stat-card">
          <div className="team-stat-value">{loadingMembers ? '—' : members.filter((m) => m.is_superuser).length}</div>
          <div className="team-stat-label">Admins</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="team-tabs">
        <button className={`team-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>
          Members
          <span className="team-tab-count">{members.length}</span>
        </button>
        <button className={`team-tab ${activeTab === 'invites' ? 'active' : ''}`} onClick={() => setActiveTab('invites')}>
          Invitations
          <span className="team-tab-count">{pendingInvites.length}</span>
        </button>
      </div>

      {/* Members Table */}
      {activeTab === 'members' && (
        <div className="team-table-card">
          {loadingMembers ? (
            <div className="team-loading">Loading team members…</div>
          ) : members.length === 0 ? (
            <div className="team-empty">No active team members found.</div>
          ) : (
            <table className="team-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="team-member-cell">
                        <div className="team-avatar">{getInitials(member)}</div>
                        <div>
                          <div className="team-member-name">
                            {member.first_name || member.last_name
                              ? `${member.first_name} ${member.last_name}`.trim()
                              : member.username}
                          </div>
                          <div className="team-member-username">@{member.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="team-cell-muted">{member.email || '—'}</td>
                    <td>
                      <span className={`team-role-badge ${member.is_superuser ? 'superuser' : member.is_staff ? 'staff' : 'member'}`}>
                        {member.is_superuser ? 'Super Admin' : member.is_staff ? 'Staff' : 'Member'}
                      </span>
                    </td>
                    <td className="team-cell-muted">{formatDate(member.date_joined)}</td>
                    <td className="team-cell-muted">{formatDate(member.last_login)}</td>
                    <td>
                      {currentUserIsSuperuser && !member.is_superuser && member.id !== currentUserId && (
                        <button
                          className="team-btn-remove"
                          onClick={() => handleRemoveMember(member.id, member.username)}
                          title="Remove member"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor" />
                          </svg>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invitations Table */}
      {activeTab === 'invites' && (
        <div className="team-table-card">
          {loadingInvites ? (
            <div className="team-loading">Loading invitations…</div>
          ) : invites.length === 0 ? (
            <div className="team-empty">
              No invitations yet.{' '}
              <button className="team-empty-link" onClick={() => setShowInviteModal(true)}>
                Send your first invite
              </button>
            </div>
          ) : (
            <table className="team-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Invited By</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td className="team-invite-email">{inv.email}</td>
                    <td className="team-cell-muted">{inv.invited_by || '—'}</td>
                    <td className="team-cell-muted">{formatDate(inv.created_at)}</td>
                    <td className="team-cell-muted">{formatDate(inv.expires_at)}</td>
                    <td>
                      <span className={`team-invite-status ${inv.is_expired && inv.status === 'pending' ? 'expired' : inv.status}`}>
                        {inv.is_expired && inv.status === 'pending' ? 'Expired' : inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      {inv.status === 'pending' && !inv.is_expired && (
                        <button
                          className="team-btn-remove"
                          onClick={() => handleRevokeInvite(inv.id)}
                          title="Revoke invite"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="team-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="team-modal-header">
              <div className="team-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#8b5cf6" />
                </svg>
              </div>
              <div>
                <h3 className="team-modal-title">Invite a Team Member</h3>
                <p className="team-modal-subtitle">They'll receive an email with a link to join your team.</p>
              </div>
              <button className="team-modal-close" onClick={() => setShowInviteModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            </div>

            {inviteSuccess ? (
              <div>
                <div className={`team-invite-success ${!inviteEmailSent ? 'team-invite-success--warn' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    {inviteEmailSent
                      ? <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#10b981" />
                      : <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#d97706" />}
                  </svg>
                  {inviteSuccess}
                </div>
                {!inviteEmailSent && inviteAcceptUrl && (
                  <div className="team-invite-link-box">
                    <p className="team-invite-link-label">Share this link with the invitee:</p>
                    <div className="team-invite-link-row">
                      <input readOnly className="team-invite-link-input" value={inviteAcceptUrl} onClick={e => e.target.select()} />
                      <button className="team-btn-copy" onClick={handleCopyLink}>
                        {linkCopied ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSendInvite}>
                {inviteError && <div className="team-invite-error">{inviteError}</div>}
                <div className="team-form-group">
                  <label className="team-form-label">Email address</label>
                  <input
                    type="email"
                    className="team-form-input"
                    placeholder="colleague@lawfirm.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="team-modal-actions">
                  <button type="button" className="team-btn-cancel" onClick={() => setShowInviteModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="team-btn-send" disabled={inviteLoading}>
                    {inviteLoading ? 'Sending…' : 'Send Invite'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamAdmin;
